import type { App } from '@hangar/shared';
import { Dot, FrameworkBadge, TONE_PILL, type Tone } from '@/components/badges';
import { Icon } from '@/components/Icon';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { SigningCredential } from '@/api/endpointsSigning';
import { cn } from '@/lib/utils';
import { ANDROID_SLOTS, SIGNING_SLOTS, factsOf, healthOf, slotsOf, warningsOf, type SigningSlot } from './signingModel';

/** Health as dot + label (never color alone). */
function HealthPill({ tone, label }: { tone: Tone; label: string }) {
  return (
    <Badge variant="outline" className={cn('gap-1.5 font-mono text-[11px] font-normal leading-4', TONE_PILL[tone])}>
      <Dot tone={tone} />
      {label}
    </Badge>
  );
}

export interface SlotActions {
  onUpload: (app: App, slot: SigningSlot, current: SigningCredential | null) => void;
  onRevoke: (app: App, slot: SigningSlot, current: SigningCredential) => void;
}

function SlotRow({ app, slot, credential, now, actions }: { app: App; slot: SigningSlot; credential: SigningCredential | null; now: number; actions: SlotActions | null }) {
  const health = healthOf(credential, now);
  const warnings = credential ? warningsOf(credential) : [];
  return (
    <li data-testid={`signing-slot-${app.slug}-${slot.kind}`} className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-start">
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-md border bg-secondary text-muted-foreground">
          <Icon name={slot.icon} size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-foreground">{slot.label}</span>
            <HealthPill tone={health.tone} label={health.label} />
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">{health.detail}</p>
          {credential ? <Facts credential={credential} /> : null}
          {warnings.length ? (
            <ul className="mt-2 flex flex-col gap-1 text-xs text-awaiting" aria-label="Warnings">
              {warnings.map((w) => (
                <li key={w} className="flex items-start gap-1.5">
                  <Icon name="warning" size={14} className="mt-px shrink-0" />
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
      {actions ? <RowButtons app={app} slot={slot} credential={credential} actions={actions} /> : null}
    </li>
  );
}

function Facts({ credential }: { credential: SigningCredential }) {
  return (
    <dl className="mt-2 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
      {factsOf(credential).map((f) => (
        <div key={f.label} className="contents">
          <dt className="text-subtle">{f.label}</dt>
          <dd className={cn('truncate text-foreground', f.mono && 'font-mono')} title={f.value}>
            {f.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function RowButtons({ app, slot, credential, actions }: { app: App; slot: SigningSlot; credential: SigningCredential | null; actions: SlotActions }) {
  return (
    <div className="flex shrink-0 gap-2 sm:justify-end">
      <Button variant="outline" size="sm" className="min-h-11 sm:min-h-8" data-testid={`signing-upload-${app.slug}-${slot.kind}`} onClick={() => actions.onUpload(app, slot, credential)}>
        <Icon name={credential ? 'autorenew' : 'upload'} size={16} />
        {credential ? 'Replace' : 'Upload'}
      </Button>
      {credential ? (
        <Button variant="ghost" size="sm" className="min-h-11 text-destructive sm:min-h-8" data-testid={`signing-revoke-${app.slug}-${slot.kind}`} onClick={() => actions.onRevoke(app, slot, credential)}>
          Revoke
        </Button>
      ) : null}
    </div>
  );
}

/** One app and its slots for a platform (iOS: three, Android: the keystore). `actions` null: read-only (everyone but platform operators). */
export function SigningAppCard({ app, platform = 'ios', credentials, now, actions }: { app: App; platform?: 'ios' | 'android'; credentials: SigningCredential[]; now: number; actions: SlotActions | null }) {
  const android = platform === 'android';
  return (
    <Card className="enter" data-testid={`signing-app-${app.slug}${android ? '-android' : ''}`}>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          {app.name}
          <FrameworkBadge framework={app.framework} />
          <Badge variant="outline" className="font-mono text-[11px] font-normal leading-4">
            {android ? 'Android' : 'iOS'}
          </Badge>
        </CardTitle>
        <CardDescription>{android ? 'Signs every Android build of the app, so users can always update it.' : 'Signs the app\'s App Store and ad hoc builds.'}</CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="flex flex-col divide-y">
          {slotsOf(app.id, credentials, android ? ANDROID_SLOTS : SIGNING_SLOTS).map(({ slot, credential }) => (
            <SlotRow key={slot.kind} app={app} slot={slot} credential={credential} now={now} actions={actions} />
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
